import { Link } from 'react-router-dom';
import {
    ArrowUpRight,
    Lightning,
    Barbell,
    WaveSine,
} from '@phosphor-icons/react';
import { LANDING } from '@/constants/testIds';

export default function Landing() {
    return (
        <main
            data-testid={LANDING.root}
            className="relative mx-auto max-w-6xl px-5 sm:px-8 pb-32 pt-10 sm:pt-16"
        >
            {/* Eyebrow */}
            <div className="animate-fade-up [animation-delay:60ms]">
                <span className="inline-flex items-center gap-2 rounded-full border border-foreground/10 bg-foreground/5 px-3 py-1.5 uppercase-mono">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00FF9D] animate-soft-pulse" />
                    Phase 01 · Design System
                </span>
            </div>

            {/* Hero */}
            <section className="mt-8 sm:mt-14 grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-16 items-end">
                <div className="animate-fade-up [animation-delay:140ms]">
                    <h1
                        data-testid={LANDING.heroTitle}
                        className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl"
                    >
                        Lift heavy.{' '}
                        <span className="text-muted-foreground">Track</span>{' '}
                        <span className="block">
                            ruthlessly.{' '}
                            <span className="text-[#00FF9D] [text-shadow:0_0_24px_rgba(0,255,157,0.35)]">
                                Win quietly.
                            </span>
                        </span>
                    </h1>

                    <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
                        GymFlow is the ultra-minimalist workout tracker for
                        athletes who treat training like a craft. Industrial
                        minimalism, glassmorphic depth, thumb-first ergonomics —
                        no fluff, no streaks, no badges. Just the work.
                    </p>

                    <div className="mt-9 flex flex-wrap items-center gap-3">
                        <Link
                            to="/style-guide"
                            data-testid={LANDING.enterStyleGuideBtn}
                            className="group inline-flex items-center gap-3 rounded-2xl bg-primary text-primary-foreground px-6 py-4 font-bold tracking-tight shadow-neon dark:shadow-neon hover:-translate-y-[2px] active:scale-[0.98] transition-all"
                        >
                            <span>Enter Style Guide</span>
                            <ArrowUpRight
                                size={20}
                                weight="bold"
                                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                            />
                        </Link>

                        <a
                            href="#anchor-philosophy"
                            data-testid={LANDING.secondaryLearnBtn}
                            className="inline-flex items-center gap-2 rounded-2xl border border-foreground/15 bg-foreground/5 px-6 py-4 font-semibold hover:bg-foreground/10 active:scale-[0.98] transition-all"
                        >
                            Design philosophy
                        </a>
                    </div>

                    {/* Quick stats row */}
                    <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
                        <MiniStat value="48" unit="px" label="Touch min." />
                        <MiniStat value="2" unit="" label="Modes" highlight />
                        <MiniStat value="∞" unit="" label="Reps" />
                    </div>
                </div>

                {/* Right side — accent card */}
                <div className="animate-fade-up [animation-delay:240ms]">
                    <div className="glass-card grain rounded-3xl p-6 sm:p-8 relative overflow-hidden">
                        <div className="flex items-center justify-between mb-6">
                            <span className="uppercase-mono text-muted-foreground">
                                Live · Session 042
                            </span>
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/30 text-[#00FF9D] px-2.5 py-1 uppercase-mono">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00FF9D] animate-soft-pulse" />
                                Push Day
                            </span>
                        </div>

                        <div className="flex items-baseline gap-2">
                            <span className="font-mono text-7xl sm:text-8xl font-bold tabular-nums tracking-tighter">
                                127
                            </span>
                            <span className="font-mono text-xl text-muted-foreground">
                                .5
                            </span>
                            <span className="uppercase-mono text-muted-foreground ml-2">
                                kg
                            </span>
                        </div>
                        <p className="mt-2 uppercase-mono text-muted-foreground">
                            Bench · Top set · 5 reps
                        </p>

                        <div className="mt-8 grid grid-cols-2 gap-3">
                            <FeatureChip
                                icon={<Lightning size={16} weight="fill" />}
                                label="PR detection"
                                accent="#FF4D4D"
                            />
                            <FeatureChip
                                icon={<Barbell size={16} weight="bold" />}
                                label="Plate math"
                            />
                            <FeatureChip
                                icon={<WaveSine size={16} weight="bold" />}
                                label="RPE curves"
                            />
                            <FeatureChip
                                icon={
                                    <span className="font-mono text-xs font-bold">
                                        1RM
                                    </span>
                                }
                                label="Auto estimate"
                                accent="#00FF9D"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Anchor row */}
            <section
                id="anchor-philosophy"
                className="mt-24 grid sm:grid-cols-3 gap-4 sm:gap-6 animate-fade-up [animation-delay:340ms]"
            >
                <PhilosophyCard
                    n="01"
                    title="Industrial minimalist"
                    body="Matte charcoal canvas, sharp neon accents, monospaced numerics. Built for clarity under fluorescent gym light."
                />
                <PhilosophyCard
                    n="02"
                    title="Glassmorphism, restrained"
                    body="Subtle 24px backdrop-blur, 1px translucent borders. Depth without decoration."
                />
                <PhilosophyCard
                    n="03"
                    title="Thumb-first"
                    body="Every interactive target ≥ 48px. Primary actions sit in the bottom thumb arc, always within reach."
                />
            </section>
        </main>
    );
}

function MiniStat({ value, unit, label, highlight }) {
    return (
        <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3">
            <div className="flex items-baseline gap-1">
                <span
                    className={`font-mono font-bold tabular-nums text-2xl ${
                        highlight ? 'text-[#00FF9D]' : ''
                    }`}
                >
                    {value}
                </span>
                {unit && (
                    <span className="uppercase-mono text-muted-foreground">
                        {unit}
                    </span>
                )}
            </div>
            <p className="uppercase-mono text-muted-foreground mt-1">
                {label}
            </p>
        </div>
    );
}

function FeatureChip({ icon, label, accent }) {
    return (
        <div className="flex items-center gap-2.5 rounded-2xl border border-foreground/10 bg-foreground/[0.03] px-3 py-2.5">
            <span
                className="grid place-items-center w-7 h-7 rounded-full"
                style={{
                    background: accent ? `${accent}22` : 'rgba(127,127,127,0.1)',
                    color: accent || 'currentColor',
                }}
            >
                {icon}
            </span>
            <span className="text-sm font-semibold tracking-tight">
                {label}
            </span>
        </div>
    );
}

function PhilosophyCard({ n, title, body }) {
    return (
        <div className="glass-card rounded-3xl p-6 sm:p-7 hover:-translate-y-[3px] transition-transform">
            <span className="font-mono text-xs text-muted-foreground">
                {n}
            </span>
            <h3 className="mt-2 text-xl font-display font-extrabold tracking-tight">
                {title}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {body}
            </p>
        </div>
    );
}
